import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import type { OpenQuestion } from '@localagent/sdk';
import { openQuestionsAtom } from '@localagent/sdk/react';
import { Popover } from '@localagent/ui/components/popover';
import { cn } from '@localagent/ui/lib/utils';
import { openQuestionsErrorAtom } from '../../stores/chat/atoms';
import { QUESTIONS_TAB_ID } from '../chat/chat-tabs';

function QuestionGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}

export interface NeedsInputListProps {
  questions: OpenQuestion[];
  onChoose: (sessionId: string) => void;
}

export function NeedsInputList({ questions, onChoose }: NeedsInputListProps) {
  return (
    <ul data-testid="needs-input-list" className="flex max-h-80 flex-col overflow-y-auto">
      {questions.map((q) => (
        <li key={q.question_id}>
          <button
            type="button"
            data-testid={`needs-input-entry-${q.question_id}`}
            data-session-id={q.session_id}
            onClick={() => onChoose(q.session_id)}
            className="flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left hover:bg-accent"
          >
            <span className="w-full truncate text-label font-medium text-foreground">{q.question}</span>
            <span className="w-full truncate text-micro text-muted-foreground">
              {q.asker_name} · {q.session_title}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export interface NeedsInputIndicatorProps {
  compact?: boolean;
}

export function NeedsInputIndicator({ compact = false }: NeedsInputIndicatorProps) {
  const questions = useAtomValue(openQuestionsAtom);
  const error = useAtomValue(openQuestionsErrorAtom);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (error !== null) {
    const message = `Questions that need your input failed to load: ${error.message}`;
    return (
      <div className="px-2 pb-1" data-testid="needs-input">
        <div
          data-testid="needs-input-error"
          role="alert"
          title={message}
          aria-label={message}
          className={cn(
            'flex w-full items-center gap-2 rounded-md bg-error-subtle px-2 py-1.5 text-error-text',
            compact && 'justify-center px-1',
          )}
        >
          <QuestionGlyph />
          {compact ? null : <span className="flex-1 truncate text-left text-label font-medium">Questions failed to load</span>}
        </div>
      </div>
    );
  }
  if (questions.length === 0) return null;
  const label = `${questions.length} ${questions.length === 1 ? 'question needs' : 'questions need'} your input`;
  const choose = (sessionId: string) => {
    setOpen(false);
    void navigate(`/chat/${sessionId}?tab=${QUESTIONS_TAB_ID}`);
  };
  return (
    <div className="px-2 pb-1" data-testid="needs-input">
      <Popover
        open={open}
        onOpenChange={setOpen}
        align="start"
        portal
        aria-label="Questions that need your input"
        triggerClassName={cn(
          'flex w-full items-center gap-2 rounded-md bg-warning-subtle px-2 py-1.5 text-warning-text hover:bg-warning-subtle/80',
          compact && 'justify-center px-1',
        )}
        contentClassName="w-72 p-1"
        trigger={
          <span data-testid="needs-input-indicator" title={label} aria-label={label} className="flex w-full items-center gap-2">
            <QuestionGlyph />
            {compact ? null : <span className="flex-1 text-left text-label font-medium">Needs your input</span>}
            <span data-testid="needs-input-count" className="text-micro tabular-nums">
              {questions.length}
            </span>
          </span>
        }
      >
        <NeedsInputList questions={questions} onChoose={choose} />
      </Popover>
    </div>
  );
}
